import React, { useMemo } from 'react';

import { StepProps, track } from '@betterment/js-runtime';
import { Box, Button, FormikField, H2, Input, Select, Text, VStack } from '@betterment/samba-react';
import { useFeatureFlag } from '@betterment/shared-components';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import { CustomerSignupValues } from './Controller';

import FullPageLoader from '../Shared/components/FullPageLoader';
import { CheckingSignupData, CheckingSignupDataParams } from '../Shared/data';
import { WorkflowLayout } from '../Shared/layout/WorkflowLayout';

export interface Props extends StepProps<CustomerSignupValues> {
  checkingSignupData: CheckingSignupData;
  handleClose: () => void;
  persistCheckingSignupData?: (values: CheckingSignupDataParams) => Promise<void>;
}

const LegacyStepSchema = Yup.object<{ value: string }>().shape({
  value: Yup.string().required('Occupation cannot be blank'),
});

const FORMER_EMPLOYMENT_STATUSES = new Set(['unemployed', 'retired']);

const EXCLUDED_OCCUPATIONS = new Set(['N/A', 'None', 'Other']);

export const CHECKING_FLOW_UPDATES_FLAG = 'retail.checking_account_flow_updates_q1_2026_enabled';

const STUDENT_STATUSES = new Set(['student', 'foreign_student']);

const LEGACY_TO_V2_EMPLOYMENT_STATUS: Record<string, string> = {
  full_time: 'employed',
  part_time: 'employed',
  homemaker: 'unemployed',
  disabled: 'unemployed',
};

const mapEmploymentStatusToV2 = (status: string | undefined | null): string => {
  if (!status) return '';
  return LEGACY_TO_V2_EMPLOYMENT_STATUS[status] ?? status;
};

const findMatchingRole = (role: string | undefined | null, roleOptions: string[]): string => {
  if (!role) return '';
  const lowerRole = role.toLowerCase();
  return roleOptions.find(opt => opt.toLowerCase() === lowerRole) ?? role;
};

interface InitialFormValues {
  employmentStatus: string;
  occupation: string;
  employerName: string;
}

interface GetInitialFormValuesParams {
  flowEmploymentStatus?: string;
  flowRole?: string;
  flowEmployerName?: string;
  userProfession: CheckingSignupData['userProfession'];
  roleOptions: string[];
}

const getInitialFormValues = ({
  flowEmploymentStatus,
  flowRole,
  flowEmployerName,
  userProfession,
  roleOptions,
}: GetInitialFormValuesParams): InitialFormValues => {
  const rawEmploymentStatus = flowEmploymentStatus ?? userProfession?.employmentStatus ?? '';
  const rawRole = flowRole ?? userProfession?.role ?? '';
  return {
    employmentStatus: mapEmploymentStatusToV2(rawEmploymentStatus),
    occupation: findMatchingRole(rawRole, roleOptions),
    employerName: flowEmployerName ?? userProfession?.employerName ?? '',
  };
};

interface OccupationSelectFieldProps {
  name: string;
  label: string;
  options: string[];
}

const OccupationSelectField: React.FC<OccupationSelectFieldProps> = ({ name, label, options }) => (
  <FormikField name={name}>
    <FormikField.Label>{label}</FormikField.Label>
    <FormikField.Control>
      <Select>
        <Select.Trigger aria-label={`Select ${label.toLowerCase()}`}>
          <Select.Value placeholder="Select..." />
        </Select.Trigger>
        <Select.Content>
          {options.map(value => (
            <Select.Item key={value} value={value}>
              {value}
            </Select.Item>
          ))}
        </Select.Content>
      </Select>
    </FormikField.Control>
    <FormikField.ErrorMessage />
  </FormikField>
);

interface EmployerFieldProps {
  name: string;
  label: string;
}

const EmployerField: React.FC<EmployerFieldProps> = ({ name, label }) => (
  <FormikField name={name}>
    <FormikField.Label>{label}</FormikField.Label>
    <FormikField.Control>
      <Input type="text" />
    </FormikField.Control>
    <FormikField.ErrorMessage />
  </FormikField>
);

interface OccupationStepProps extends Pick<StepProps<CustomerSignupValues>, 'goToPreviousStep' | 'goToNextStep'> {
  stepTitle: string;
  handleClose: () => void;
}

interface LegacyOccupationFormProps extends OccupationStepProps {
  initialRole: string;
  roleOptions: string[];
}

const LegacyOccupationForm: React.FC<LegacyOccupationFormProps> = ({
  stepTitle,
  initialRole,
  roleOptions,
  goToPreviousStep,
  goToNextStep,
  handleClose,
}) => {
  const handleSubmit = ({ value }: { value: string }) => {
    track('ElementClicked', { name: 'Continue', flow: 'OpenCheckingAccount' });
    goToNextStep({ role: value });
  };

  return (
    <WorkflowLayout
      feature="checking_customer_signup"
      pageTitle="Sign up - Checking"
      workflowTitle={stepTitle}
      onBack={goToPreviousStep}
      onClose={handleClose}
    >
      <H2 color="text">Tell us about your employment.</H2>
      <Box css={{ marginY: 'space.200' }}>
        <Text as="p" color="text" css={{ marginBottom: 'space.300' }}>
          We&apos;re required by our regulators to collect this information.
        </Text>
      </Box>
      <Formik
        onSubmit={handleSubmit}
        validationSchema={LegacyStepSchema}
        initialValues={{ value: initialRole }}
        validateOnBlur={false}
        validateOnChange={false}
      >
        {({ isSubmitting }) => (
          <Form>
            <VStack css={{ gap: 'space.300', width: '100%' }}>
              <OccupationSelectField name="value" label="Occupation" options={roleOptions} />
            </VStack>
            <Button
              type="submit"
              css={{ mt: 'space.400', width: { base: '100%', md: 'auto' } }}
              disabled={isSubmitting}
            >
              Continue
            </Button>
          </Form>
        )}
      </Formik>
    </WorkflowLayout>
  );
};

interface NewOccupationFormProps extends OccupationStepProps {
  initialEmploymentStatus: string;
  initialOccupation: string;
  initialEmployerName: string;
  filteredRoleOptions: string[];
  employmentStatusOptions: Record<string, string>;
  sourceOfFunds?: string;
  persistCheckingSignupData?: (values: CheckingSignupDataParams) => Promise<void>;
}

const NewOccupationForm: React.FC<NewOccupationFormProps> = ({
  stepTitle,
  initialEmploymentStatus,
  initialOccupation,
  initialEmployerName,
  filteredRoleOptions,
  employmentStatusOptions,
  sourceOfFunds,
  goToPreviousStep,
  goToNextStep,
  handleClose,
  persistCheckingSignupData,
}) => {
  const NewFlowStepSchema = useMemo(() => {
    return Yup.object().shape({
      employmentStatus: Yup.string().required('Employment status is required'),
      role: Yup.string().when('employmentStatus', (values, schema) =>
        STUDENT_STATUSES.has(String(values[0])) ? schema : schema.required('Occupation is required')
      ),
      employerName: Yup.string().when('employmentStatus', (values, schema) =>
        STUDENT_STATUSES.has(String(values[0])) ? schema : schema.required('Employer is required')
      ),
    });
  }, []);

  const handleSubmit = async (values: { employmentStatus: string; role: string; employerName: string }) => {
    track('ElementClicked', { name: 'Continue', flow: 'OpenCheckingAccount' });
    const isStudent = STUDENT_STATUSES.has(values.employmentStatus);

    if (persistCheckingSignupData) {
      await persistCheckingSignupData({
        fundingSourceOption: sourceOfFunds ?? '',
        roleOption: isStudent ? undefined : values.role,
        employmentStatus: values.employmentStatus,
        employerName: isStudent ? undefined : values.employerName || undefined,
      });
    }

    goToNextStep({
      role: isStudent ? '' : values.role,
      employmentStatus: values.employmentStatus,
      employerName: isStudent ? '' : values.employerName,
    });
  };

  return (
    <WorkflowLayout
      feature="checking_customer_signup"
      pageTitle="Sign up - Checking"
      workflowTitle={stepTitle}
      onBack={goToPreviousStep}
      onClose={handleClose}
    >
      <H2 color="text">Confirm your employment.</H2>
      <Box css={{ marginY: 'space.200' }}>
        <Text as="p" color="text" css={{ marginBottom: 'space.100' }}>
          We&apos;re required by our regulators to collect this information.
        </Text>
      </Box>
      <Formik
        onSubmit={handleSubmit}
        validationSchema={NewFlowStepSchema}
        initialValues={{
          employmentStatus: initialEmploymentStatus,
          role: initialOccupation,
          employerName: initialEmployerName,
        }}
        validateOnBlur={false}
        validateOnChange={false}
      >
        {({ isSubmitting, values: formValues }) => {
          const isStudentStatus = STUDENT_STATUSES.has(formValues.employmentStatus);
          const isFormerEmployment = FORMER_EMPLOYMENT_STATUSES.has(formValues.employmentStatus);
          const occupationLabel = isFormerEmployment ? 'Former occupation' : 'Occupation';
          const employerLabel = isFormerEmployment ? 'Former employer' : 'Employer';

          return (
            <Form>
              <VStack css={{ gap: 'space.300', width: '100%' }}>
                <FormikField name="employmentStatus">
                  <FormikField.Label>Employment status</FormikField.Label>
                  <FormikField.Control>
                    <Select>
                      <Select.Trigger aria-label="Select employment status">
                        <Select.Value placeholder="Select..." />
                      </Select.Trigger>
                      <Select.Content>
                        {Object.entries(employmentStatusOptions).map(([key, displayName]) => (
                          <Select.Item key={key} value={key}>
                            {displayName}
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select>
                  </FormikField.Control>
                  <FormikField.ErrorMessage />
                </FormikField>

                {!isStudentStatus && (
                  <>
                    <Box>
                      <OccupationSelectField name="role" label={occupationLabel} options={filteredRoleOptions} />
                      {formValues.employmentStatus === 'unemployed' && (
                        <Text as="p" color="text.weak" css={{ mt: 'space.100' }}>
                          Please choose the option that best describes your situation.
                        </Text>
                      )}
                    </Box>
                    <EmployerField name="employerName" label={employerLabel} />
                  </>
                )}
              </VStack>

              <Button
                type="submit"
                css={{ mt: 'space.400', width: { base: '100%', md: 'auto' } }}
                disabled={isSubmitting}
              >
                Continue
              </Button>
            </Form>
          );
        }}
      </Formik>
    </WorkflowLayout>
  );
};

const Occupation: React.FC<React.PropsWithChildren<Props>> = ({
  flowState,
  goToNextStep,
  goToPreviousStep,
  checkingSignupData,
  handleClose,
  persistCheckingSignupData,
}) => {
  const {
    stepTitle,
    role: initialRole,
    employmentStatus: flowEmploymentStatus,
    employerName: flowEmployerName,
    sourceOfFunds,
  } = flowState.values;

  const { enabled: isNewFlowEnabled, loading: isFeatureFlagLoading } = useFeatureFlag({
    splitName: CHECKING_FLOW_UPDATES_FLAG,
    context: 'checking_signup',
  });

  const filteredRoleOptions = useMemo(
    () => checkingSignupData.roleOptions.filter(opt => !EXCLUDED_OCCUPATIONS.has(opt)),
    [checkingSignupData.roleOptions]
  );

  const employmentStatusOptions = useMemo(
    () => Object.fromEntries((checkingSignupData.employmentStatusOptions ?? []).map(opt => [opt.key, opt.displayName])),
    [checkingSignupData.employmentStatusOptions]
  );

  if (isFeatureFlagLoading) {
    return <FullPageLoader />;
  }

  if (!isNewFlowEnabled) {
    return (
      <LegacyOccupationForm
        stepTitle={stepTitle}
        initialRole={initialRole ?? ''}
        roleOptions={checkingSignupData.roleOptions}
        goToPreviousStep={goToPreviousStep}
        goToNextStep={goToNextStep}
        handleClose={handleClose}
      />
    );
  }

  const {
    employmentStatus: initialEmploymentStatus,
    occupation: initialOccupation,
    employerName: initialEmployerName,
  } = getInitialFormValues({
    flowEmploymentStatus,
    flowRole: initialRole,
    flowEmployerName,
    userProfession: checkingSignupData.userProfession,
    roleOptions: checkingSignupData.roleOptions,
  });

  return (
    <NewOccupationForm
      stepTitle={stepTitle}
      initialEmploymentStatus={initialEmploymentStatus}
      initialOccupation={initialOccupation}
      initialEmployerName={initialEmployerName}
      filteredRoleOptions={filteredRoleOptions}
      employmentStatusOptions={employmentStatusOptions}
      sourceOfFunds={sourceOfFunds}
      goToPreviousStep={goToPreviousStep}
      goToNextStep={goToNextStep}
      handleClose={handleClose}
      persistCheckingSignupData={persistCheckingSignupData}
    />
  );
};

export default Occupation;
